import React, { useEffect, useState } from "react";
import {
  database,
  ref,
  onValue,
  push,
  set,
  update,
  remove,
} from "./firebase";
import { get } from "firebase/database"; // 🔹 for reading after delete
import "./BillingSystem.css";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

const products = [
  "Premium 2 Litre Bottle",
  "Premium 1 Litre Bottle",
  "Premium 500ML Bottle",
  "Premium 250ML Bottle",
  "Green 1 Litre Bottle",
  "Green 500ML Bottle",
  "250ML Pouch",
  "Packed Ice Cube (500 gm)",
];

export default function OpeningStock({ role, phone }) {
  const [userName, setUserName] = useState(phone || "");
  const [notification, setNotification] = useState("");
  const [stockValues, setStockValues] = useState(
    Array(products.length).fill(0)
  );
  const [history, setHistory] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 3000);
  };

  const formatDate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const formatTime = (dateObj) => {
    return dateObj.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Load user name and opening stock history
  useEffect(() => {
    const userRef = ref(database, `users/${phone}`);
    onValue(
      userRef,
      (snap) => {
        const data = snap.val();
        if (data?.name) setUserName(data.name);
      },
      { onlyOnce: true }
    );

    const historyRef = ref(database, "openingStockHistory");
    onValue(historyRef, (snap) => {
      const raw = snap.val() || {};
      const list = Object.entries(raw).map(([id, val]) => ({
        id,
        ...val,
      }));
      // latest first
      list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setHistory(list);
    });
  }, [phone]);

  const handleValueChange = (index, value) => {
    const arr = [...stockValues];
    arr[index] = Math.max(0, Number(value) || 0);
    setStockValues(arr);
  };

  const handleSaveOpeningStock = async () => {
    const hasAny = stockValues.some((v) => v > 0);
    if (!hasAny) {
      showNotification("Enter at least one opening stock value");
      return;
    }

    const now = new Date();
    const items = products
      .map((product, i) => ({
        product,
        cartons: stockValues[i] || 0,
      }))
      .filter((it) => it.cartons > 0);

    const entry = {
      date: formatDate(now),
      time: formatTime(now),
      timestamp: now.getTime(),
      createdBy: userName,
      items,
    };

    const historyRef = ref(database, "openingStockHistory");

    if (editingId) {
      await update(ref(database, `openingStockHistory/${editingId}`), entry);
      showNotification("Opening stock entry updated");
    } else {
      await push(historyRef, entry);
      showNotification("Opening stock saved");
    }

    // Update current openingStock node (cartons per product)
    const openingMap = {};
    products.forEach((p, i) => {
      openingMap[p] = stockValues[i] || 0;
    });
    await set(ref(database, "openingStock"), openingMap);

    // Reset form
    setEditingId(null);
    setStockValues(Array(products.length).fill(0));
  };

  const handleEditEntry = (entry) => {
    const arr = Array(products.length).fill(0);
    (entry.items || []).forEach(({ product, cartons }) => {
      const idx = products.indexOf(product);
      if (idx >= 0) {
        arr[idx] = Number(cartons) || 0;
      }
    });
    setStockValues(arr);
    setEditingId(entry.id);
    window.scrollTo(0, 0);
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Delete this opening stock entry?")) return;

    // 1️⃣ Delete from history in Realtime Database
    await remove(ref(database, `openingStockHistory/${id}`));

    // 2️⃣ Recalculate current openingStock from latest remaining history
    const historyRef = ref(database, "openingStockHistory");
    const snap = await get(historyRef);
    const raw = snap.val() || {};

    let latest = null;
    Object.entries(raw).forEach(([entryId, val]) => {
      if (!latest || (val.timestamp || 0) > (latest.timestamp || 0)) {
        latest = { id: entryId, ...val };
      }
    });

    const openingMap = {};
    products.forEach((p) => {
      openingMap[p] = 0;
    });

    if (latest && latest.items) {
      latest.items.forEach(({ product, cartons }) => {
        if (openingMap.hasOwnProperty(product)) {
          openingMap[product] = Number(cartons) || 0;
        }
      });
    }

    await set(ref(database, "openingStock"), openingMap);

    showNotification("Opening stock entry deleted");
  };

  const filteredHistory = history.filter((h) => {
    const text =
      `${h.date || ""} ${h.time || ""} ${h.createdBy || ""}`.toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="container">
      <h2>Opening Stock</h2>
   <BossNavigation />
      <Navigation role={role} />

      {notification && (
        <div className="notification-banner">{notification}</div>
      )}

      <p>
        Logged in as <strong>{userName}</strong> ({role})
      </p>

      {["owner", "manager", "boss", "production", "stock manager"].includes(role) && (
        <>
          <div className="section">
            <h4>Set Opening Stock (Cartons)</h4>
            <div className="product-row-space">
              <span className="product-label">
                <strong>Product</strong>
              </span>
              <span className="product-input-heading">
                <strong>Cartons</strong>
              </span>
            </div>
            {products.map((p, i) => (
              <div key={i} className="product-row-space">
                <span className="product-label">{p}</span>
                <input
                  type="number"
                  className="product-input-box"
                  value={stockValues[i]}
                  onChange={(e) => handleValueChange(i, e.target.value)}
                  min="0"
                />
              </div>
            ))}
            <button onClick={handleSaveOpeningStock}>
              {editingId ? "Update Opening Stock Entry" : "Save Opening Stock"}
            </button>
          </div>

          <div className="section">
            <h4>Opening Stock History</h4>
            <input
              placeholder="Search by date / time / user"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {filteredHistory.length === 0 ? (
              <p>No opening stock entries found.</p>
            ) : (
              filteredHistory.map((entry) => (
                <div key={entry.id} className="token-card">
                  <strong>
                    {entry.date} - {entry.time}
                  </strong>
                  <br />
                  <small>
                    <strong>Created By:</strong> {entry.createdBy}
                  </small>
                  <ul>
                    {(entry.items || []).map((item, idx) => (
                      <li key={idx}>
                        {item.product}: {item.cartons} cartons
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => handleEditEntry(entry)}>Edit</button>
                  <button
                    onClick={() => handleDeleteEntry(entry.id)}
                    style={{ marginLeft: 8 }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
